import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  convertCompassToHighchartsDataFormat,
  getDataSources,
} from '../utils/compass'
import { Palette } from '../utils/constants'
import { capitalizeString } from '../utils/string'
import {
  BaseContext,
  commonPercentualOptions,
  commonPercentualSeriesConfig,
} from './'

export interface ChartData extends CompassAPIDataBase {
  data: {
    name: string
    id: string
    data: {
      date: string
      value: number
    }[]
  }[]
  title: string
}

const CVM_CHARTS_PATH = '/api/charts/cvm'

export namespace Cvm {
  export namespace MainInfos {
    const CONTEXT_ID = 'equity:cvm:main_infos'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      cnpjFund?: string
      startDate: string
      endDate: string
      ticker?: string
      chart: 'main_infos' | 'ticker_comparison'
      title?: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url:
            config.chart === 'main_infos'
              ? CVM_CHARTS_PATH + '/by-fund/main-infos'
              : CVM_CHARTS_PATH + '/by-ticker/ticker-comparison',
          method: 'POST',
          data: {
            ...(config.chart === 'main_infos'
              ? { cnpj_fund: config.cnpjFund }
              : { ticker: config.ticker }),
            start_date: config.startDate,
            end_date: config.endDate,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        ...commonPercentualSeriesConfig,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `series-${idx}`,
        color: chartPalette[idx],
        name: capitalizeString(s.name.split('_').join(' ')),
      }))

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: '',
        options: mergeDeepAll([
          creditsConfig,
          commonPercentualOptions,
          {
            series,
          },
        ]),
        stockTools: false,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace TopBottom {
    const CONTEXT_ID = 'equity:cvm:top_bottom'

    export type TopBottomChartId =
      | 'top_bottom_ticker_attribution'
      | 'top_bottom_excess_att'
      | 'top_bottom_excess_att_by_sector'
      | 'top_bottom_excess_avg_exposure'
      | 'top_bottom_excess_avg_exposure_by_sector'

    const TOP_BOTTOM_CHART_PATH: Record<TopBottomChartId, string> = {
      top_bottom_ticker_attribution: '/by-fund/top-bottom-ticker-attribution',
      top_bottom_excess_att: '/by-fund/top-bottom-excess-att',
      top_bottom_excess_att_by_sector:
        '/by-fund/top-bottom-excess-att-by-sector',
      top_bottom_excess_avg_exposure: '/by-fund/top-bottom-excess-avg-exposure',
      top_bottom_excess_avg_exposure_by_sector:
        '/by-fund/top-bottom-excess-avg-exposure-by-sector',
    }

    const CHART_TITLES: Record<TopBottomChartId, string> = {
      top_bottom_ticker_attribution: 'Melhores/Piores atribuições',
      top_bottom_excess_att: 'ER vs. Long bias (by ticker)',
      top_bottom_excess_att_by_sector: 'ER vs. Long bias (by sector)',
      top_bottom_excess_avg_exposure: 'OW/UW vs. Long bias (by ticker)',
      top_bottom_excess_avg_exposure_by_sector:
        'OW/UW vs. Long bias (by sector)',
    }

    export const __typelist: {
      config: ChartConfig
      data: TopBottomChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      TopBottomChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      cnpjFund: string
      startDate: string
      endDate: string
      chart: TopBottomChartId
      title?: string
    }

    interface TopBottomPoint {
      name: string
      value: number
    }

    interface TopBottomData {
      bottom: TopBottomPoint[]
      top: TopBottomPoint[]
    }

    export interface TopBottomChartData extends CompassAPIDataBase {
      data: TopBottomData
      title: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: CVM_CHARTS_PATH + TOP_BOTTOM_CHART_PATH[config.chart],
          method: 'POST',
          data: {
            cnpj_fund: config.cnpjFund,
            start_date: config.startDate,
            end_date: config.endDate,
          },
        })
      )) as AxiosResponse<TopBottomChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: TopBottomChartData) => {
      const series = [
        {
          data: data.data
            ? [
                ...data.data.top,
                { name: '', value: 0 },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: column.name,
                y: column.value * 100,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.2f} %' },
          color: Palette.green,
          showInLegend: false,
          yAxis: 'primary',
        },
      ]

      const tooltip = {
        shared: true,
        split: undefined,
        valueDecimals: 2,
        valueSuffix: '%',
      }

      const xAxis = [
        {
          type: 'category' as const,
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
        },
      ]

      const yAxis = [
        { id: 'primary', title: { text: '' }, labels: { format: '{value}%' } },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: CHART_TITLES[config.chart],
        options: mergeDeepAll([
          creditsConfig,
          commonPercentualOptions,
          {
            chart: { type: 'bar' },
            colors: chartPalette,
            series,
            stockTools: { gui: { enabled: false } },
            tooltip,
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
  export namespace Fatores {
    const CONTEXT_ID = 'equity:cvm:fatores'

    export type FatoresChartId =
      | 'quality'
      | 'non_quality'
      | 'commodity'
      | 'non_commodity'
      | 'value'
      | 'growth'
      | 'top10'
      | 'very_short_list'
      | 'focus_list'
      | 'short_list'

    const FATORES_CHART_PATH: Record<FatoresChartId, string> = {
      quality: '/fatores/quality',
      non_quality: '/fatores/non-quality',
      commodity: '/fatores/commodity',
      non_commodity: '/fatores/non-commodity',
      value: '/fatores/value',
      growth: '/fatores/growth',
      top10: '/fatores/top10',
      very_short_list: '/fatores/very-short-list',
      focus_list: '/fatores/focus-list',
      short_list: '/fatores/short-list',
    }

    const CHART_TITLES: Record<FatoresChartId, string> = {
      quality: 'Quality',
      non_quality: 'Non-Quality',
      commodity: 'Commodity',
      non_commodity: 'Non-Commodity',
      value: 'Value',
      growth: 'Growth',
      top10: 'Top 10',
      very_short_list: 'Very short list',
      focus_list: 'Focus list',
      short_list: 'Short list',
    }

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: FatoresChartId
      funds: string[]
      title?: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: CVM_CHARTS_PATH + FATORES_CHART_PATH[config.chart],
          method: 'POST',
          data: { funds: config.funds },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        ...commonPercentualSeriesConfig,
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `series-${idx}`,
        name: s.name,
        color: chartPalette[idx],
        yAxis: 'primary',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: CHART_TITLES[config.chart],
        options: mergeDeepAll([
          creditsConfig,
          commonPercentualOptions,
          { series, yAxis: { id: 'primary', title: { text: '' } } },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
